import {
  Attributes,
  AttributesRow,
  Card,
  DataStatus,
  FormSubmitHandler,
  closeCurrentDialog,
  openDialog,
} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {
  PaymentSettingsRequestBody,
  useGetCustomerV2Query,
  usePutCustomerPaymentSettingsMutation,
} from '@dms/api/customer';
import {
  useGetCustomerPaymentQuery,
  usePatchCustomerPaymentMutation,
  usePostCustomerPaymentMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {EMPTY_PLACEHOLDER, TestIdProps, buildArray, suffixTestId} from 'shared/web';

import {PaymentSettings} from '../types/paymentSettingsFormSchema';
import {IN_EUROPE, OUT_OF_EUROPE} from '../utils/const';
import {PaymentSettingsForm} from './PaymentSettingsForm';

interface PaymentSettingsCardProps extends TestIdProps {
  customerId: string;
}

export function PaymentSettingsCard(props: PaymentSettingsCardProps) {
  const {data, isLoading, isError} = useGetCustomerPaymentQuery({customerId: props.customerId});
  const {
    data: customerData,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useGetCustomerV2Query({customerId: props.customerId});

  const [patchCustomerPayment] = usePatchCustomerPaymentMutation();
  const [postCustomerPayment] = usePostCustomerPaymentMutation();
  const [patchCustomerAbroadSale] = usePutCustomerPaymentSettingsMutation();
  const hasAbroadSale = isNotNil(customerData?.abroadSale);

  const [canEditBankTransferSettings] = usePermissions({permissionKeys: ['allowBankTransfer']});

  const handleSubmit: FormSubmitHandler<PaymentSettings> = async (formData) => {
    let isError: boolean = false;
    const abroadSalePayload = match({
      allowAbroadSale: formData.allowAbroadSale ?? false,
      hasAbroadSale,
    })
      .returnType<PaymentSettingsRequestBody | null>()
      .with({allowAbroadSale: true}, () => ({abroadSale: formData.abroadSale?.[0] ?? null}))
      .with({hasAbroadSale: true}, () => ({abroadSale: null}))
      .otherwise(() => null);

    if (isNotNil(abroadSalePayload)) {
      await patchCustomerAbroadSale({
        customerId: props.customerId,
        paymentSettingsRequestBody: abroadSalePayload,
      })
        .unwrap()
        .catch((error: Error) => {
          handleApiError(error);
          isError = true;
        });
    }
    if (canEditBankTransferSettings) {
      const body = {
        allowBankTransfer: formData.allowBankTransfer,
        allowElectronicInvoiceEmail: formData.allowElectronicInvoiceEmail,
        email: formData.allowElectronicInvoiceEmail ? formData.email : null,
        invoiceMaturity: formData.allowBankTransfer ? (formData.invoiceMaturity ?? null) : null,
        paymentLimit: formData.allowBankTransfer ? formData.paymentLimit : null,
      };

      await (
        isNil(data)
          ? postCustomerPayment({customerId: props.customerId, body})
          : patchCustomerPayment({customerId: props.customerId, body})
      )
        .unwrap()
        .catch((error: Error) => {
          handleApiError(error);
          isError = true;
        });
    }
    if (!isError) {
      closeCurrentDialog();
    }
  };

  const handleEdit = () =>
    openDialog(
      <PaymentSettingsForm
        canEditBankTransferSettings={canEditBankTransferSettings}
        defaultValues={{
          allowBankTransfer: data?.allowBankTransfer ?? false,
          allowElectronicInvoiceEmail: data?.allowElectronicInvoiceEmail ?? false,
          email: data?.email ?? undefined,
          invoiceMaturity: data?.invoiceMaturity ?? undefined,
          allowAbroadSale: isNotNil(customerData?.abroadSale) ?? false,
          abroadSale: match(customerData?.abroadSale)
            .returnType<(typeof IN_EUROPE | typeof OUT_OF_EUROPE)[]>()
            .with(OUT_OF_EUROPE, (value) => [value])
            .otherwise(() => [IN_EUROPE]),
          paymentLimit: data?.paymentLimit ?? undefined,
        }}
        onClose={closeCurrentDialog}
        onSubmit={handleSubmit}
      />,
      {title: i18n.t('entity.customerPaymentSettings.labels.paymentSettings')}
    );

  const isBankTransferAllowed = data?.allowBankTransfer;

  const abroadSaleName =
    customerData?.abroadSale === IN_EUROPE
      ? i18n.t('entity.customerPaymentSettings.labels.saleToEU')
      : i18n.t('entity.customerPaymentSettings.labels.saleOutsideEU');

  const attributes = buildArray<AttributesRow>([])
    .add({
      label: i18n.t('entity.customerPaymentSettings.labels.abroadSale'),
      value: hasAbroadSale ? abroadSaleName : i18n.t('general.labels.notAllowed'),
    })
    .add({
      label: i18n.t('entity.invoice.paymentMethod.bankTransfer'),
      value: isBankTransferAllowed
        ? i18n.t('general.labels.allowed')
        : i18n.t('general.labels.notAllowed'),
    })
    .when(isBankTransferAllowed, {
      label: i18n.t('entity.customerPaymentSettings.labels.invoiceMaturity'),
      value: data?.invoiceMaturity ?? EMPTY_PLACEHOLDER,
    })
    .when(isBankTransferAllowed, {
      label: i18n.t('entity.customerPaymentSettings.labels.paymentLimit'),
      value: data?.paymentLimit ?? EMPTY_PLACEHOLDER,
    })
    .when(data?.allowElectronicInvoiceEmail, {
      label: i18n.t('entity.customerPaymentSettings.labels.email'),
      value: data?.email ?? EMPTY_PLACEHOLDER,
    });

  return (
    <Card
      title={i18n.t('entity.customerPaymentSettings.labels.paymentSettings')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'image/edit',
          title: i18n.t('general.actions.edit'),
          onClick: handleEdit,
          'data-testid': suffixTestId('edit', props),
        },
      ]}
    >
      <DataStatus isLoading={isLoading || isCustomerLoading} isError={isError || isCustomerError}>
        <Attributes rows={attributes} data-testid={suffixTestId('attributes', props)} />
      </DataStatus>
    </Card>
  );
}
